import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User } from '../../types';
import api from '../../config/api';
import { authService } from '../../services/api/auth.service';

// Initialize token from localStorage
const savedToken = localStorage.getItem('token');

const initialState: AuthState = {
  user: null,
  token: savedToken,
  isLoading: false,
  error: null,
};

const mapUserDataToAuthUser = (userData: {
  id: string;
  email: string;
  username?: string | null;
  is_admin?: boolean | null;
}): User => ({
  id: userData.id,
  username: userData.username || userData.email?.split('@')[0] || '',
  email: userData.email,
  role: userData.is_admin ? 'admin' : 'user',
  name: userData.username || userData.email?.split('@')[0] || 'User',
  is_admin: userData.is_admin || false,
});

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }) => {
    const response = await api.post('/auth/login', credentials);
    const { token } = response.data;
    // Store token in localStorage
    localStorage.setItem('token', token);
    const user = mapUserDataToAuthUser(await authService.getCurrentUser());
    console.log('Login successful, token stored');
    return { token, user };
  }
);

export const loginWithUrl = createAsyncThunk(
  'auth/loginWithUrl',
  async (credentials: { email: string }) => {
    try {
      const response = await api.post('/auth/login-url', credentials);
      const { token } = response.data;
      // Store token in localStorage
      localStorage.setItem('token', token);
      const user = mapUserDataToAuthUser(await authService.getCurrentUser());
      console.log('URL Login successful, token stored');
      return { token, user };
    } catch (error) {
      console.error('URL login error:', error);
      throw error;
    }
  }
);

export const getUserProfile = createAsyncThunk(
  'auth/getUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching user profile...');
      // Use auth service to get current user
      const userData = await authService.getCurrentUser();
      console.log('User profile fetched successfully:', userData);

      const user = mapUserDataToAuthUser(userData);

      return { user };
    } catch (error: any) {
      console.error('Failed to get user profile:', error);
      // Only remove token if we get a 401 unauthorized error
      if (error.response?.status === 401) {
        console.log('Unauthorized access, removing token');
        localStorage.removeItem('token');
      }
      return rejectWithValue('Failed to get user profile');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    // Try to call logout API
    await authService.logout();
    console.log('Logout API call successful');
  } catch (error) {
    console.error('Logout API error:', error);
  } finally {
    // Always remove the token from localStorage
    localStorage.removeItem('token');
    console.log('Token removed from localStorage');
  }
  return { success: true };
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Add a manual setter for token in case we need it
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Login failed';
      })
      .addCase(loginWithUrl.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithUrl.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginWithUrl.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'URL login failed';
      })
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        // If we've successfully gotten user profile, ensure token is set
        if (!state.token) {
          state.token = localStorage.getItem('token');
        }
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get user profile';
        // Clear token only if API returned 401 (handled in the thunk)
        state.token = localStorage.getItem('token');
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isLoading = false;
      });
  },
});

export const { clearError, setToken } = authSlice.actions;
export default authSlice.reducer; 